import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { QueryParams } from "@router/route-params";
import { getBooleanQueryParam } from "@utils/url-utils";

/** State for dev feature toggles */
export type DevFeaturesState = {
  /** Enable sending new phrases to Localize from dev environments */
  isTranslationOverrideAllowed: boolean;

  /** Enable dark mode theme */
  isDarkModeEnabled: boolean;

  /** Enable sending errors to Sentry from numbered environments or localhost */
  isSentryEnabled: boolean;
};

/** Initial state for dev features slice */
export const initialDevFeaturesState: DevFeaturesState = {
  isTranslationOverrideAllowed: getBooleanQueryParam(
    globalThis.location.search,
    QueryParams.allowTranslationOverride
  ),
  isDarkModeEnabled: getBooleanQueryParam(
    globalThis.location.search,
    QueryParams.enableDarkMode
  ),
  isSentryEnabled: getBooleanQueryParam(
    globalThis.location.search,
    QueryParams.enableSentry
  ),
};

/** Slice to manage dev features state */
const devFeaturesSlice = createSlice({
  name: "devFeatures",
  initialState: initialDevFeaturesState,
  reducers: {
    /** Sets whether translation override is allowed */
    setIsTranslationOverrideAllowed(state, action: PayloadAction<boolean>) {
      state.isTranslationOverrideAllowed = action.payload;
    },

    /** Sets whether dark mode is enabled */
    setIsDarkModeEnabled(state, action: PayloadAction<boolean>) {
      state.isDarkModeEnabled = action.payload;
    },

    /** Sets whether Sentry errors should be sent from numbered environments or localhost */
    setIsSentryEnabled(state, action: PayloadAction<boolean>) {
      state.isSentryEnabled = action.payload;
    },

    /** Resets the dev features state to its initial values */
    resetDevFeaturesState: () => initialDevFeaturesState,
  },
});

export const {
  setIsTranslationOverrideAllowed,
  setIsDarkModeEnabled,
  setIsSentryEnabled,
  resetDevFeaturesState,
} = devFeaturesSlice.actions;

export const devFeaturesReducer = devFeaturesSlice.reducer;
